import {
  Anchor,
  Bookmark,
  Calendar,
  Camera,
  Cloud,
  Coffee,
  Compass,
  Crown,
  Flame,
  Gem,
  Leaf,
  type LucideIcon,
  Moon,
  Mountain,
  Music,
  Palette,
  Puzzle,
  Rocket,
  Snowflake,
  Sprout,
  Sun,
  Target,
  Trophy,
} from 'lucide-react';

import { CHAT_DAY_ICON, type ConversationList, type ListColor, type ListIcon } from './useLists.js';
import styles from './ListBadge.module.css';

/**
 * The glyph behind each key. Kept here rather than in `components/Icon.tsx` on
 * purpose: that file is the vocabulary the app speaks with, where a name maps
 * to a meaning the product has decided. These are the opposite — twenty shapes
 * chosen for having no meaning, so that whatever an employer decides one means
 * is the only thing it says. Mixing the two registers is how a list ends up
 * offering the megaphone that means "info message" everywhere else.
 */
const GLYPHS: Record<ListIcon, LucideIcon> = {
  flame: Flame,
  rocket: Rocket,
  target: Target,
  trophy: Trophy,
  crown: Crown,
  gem: Gem,
  anchor: Anchor,
  compass: Compass,
  leaf: Leaf,
  sprout: Sprout,
  sun: Sun,
  moon: Moon,
  cloud: Cloud,
  snowflake: Snowflake,
  coffee: Coffee,
  music: Music,
  camera: Camera,
  palette: Palette,
  puzzle: Puzzle,
  mountain: Mountain,
  // The one glyph here that does mean something, and the exception that proves
  // the rule above: a Chat Day's list is not a set somebody made up a meaning
  // for, it is the guests of a booking, and it wears the calendar the Event
  // section is reached by. It is unreachable from the picker — see CHAT_DAY_ICON.
  [CHAT_DAY_ICON]: Calendar,
};

/**
 * CSS Modules are typed as a string index, so `styles[color]` is unchecked in
 * both directions — renaming a ListColor would silently yield undefined and
 * drop the tone, leaving a bare glyph on no chip at all. This map turns that
 * into a compile error.
 *
 * A tone class paints nothing on its own: it sets `--list-tint` and
 * `--list-ink`, and whatever wears it decides which of the two it uses. That is
 * what lets the chip, the picker's swatches and the picker's icon grid all be
 * "the violet one" without three maps agreeing about what violet is.
 */
const TONES: Record<ListColor, string> = {
  violet: styles.violet!,
  teal: styles.teal!,
  fuchsia: styles.fuchsia!,
  slate: styles.slate!,
};

/** The tone for a colour, or the default dress when none was chosen. */
export function listTone(color: ListColor | undefined): string {
  return color ? TONES[color] : styles.default!;
}

/**
 * The glyph inside the chip.
 *
 * Well under half the disc on purpose: the tint is what gets recognised at a
 * glance down a column, and it needs room to be a colour rather than a ring
 * around a drawing. The disc's own diameter is CSS (`--chip`, see
 * ListBadge.module.css) because things outside the chip have to line up with
 * its edge; only the glyph needs a number here, since an SVG is sized in props.
 */
const GLYPH = 16;

interface ListBadgeProps {
  /** Only what dresses the list, so a preview can pass a draft that isn't saved. */
  list: Pick<ConversationList, 'icon' | 'color'>;
}

/**
 * A saved list, as the one mark that identifies it.
 *
 * This replaced the row of member faces in the lists index, and the difference
 * is the point: faces answer "who is in it", which the count already says and
 * which changes every time somebody is added — so the row an employer was
 * scanning for looked different each week. A chosen glyph and colour hold
 * still, and holding still is the whole job of a thing you are looking for.
 *
 * Undressed lists are not a lesser state. A list made in passing from a picker
 * has no icon and no colour and gets the bookmark on the accent, which is what
 * every list looked like before any of this existed.
 */
export function ListBadge({ list }: ListBadgeProps) {
  return (
    <span className={`${styles.badge} ${listTone(list.color)}`}>
      <ListGlyph icon={list.icon} size={GLYPH} />
    </span>
  );
}

/**
 * One glyph, undressed. The bookmark stands in for "no icon chosen" here as
 * everywhere else, so the picker's default tile and an undressed list's chip
 * cannot come to show different things.
 */
export function ListGlyph({ icon, size }: { icon?: ListIcon; size: number }) {
  const Glyph = icon ? GLYPHS[icon] : Bookmark;
  return <Glyph size={size} strokeWidth={2} />;
}
